import React from "react";
import { useTranslation } from "react-i18next";
import { EmailBuilderColorOverrideField } from "../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import type { BorderStyle, CornerRadius, ImageOverlay } from "../domain/types";
import { DEFAULT_IMAGE_OVERLAY } from "../domain/image-overlay";
import { BackgroundImageField } from "./controls/BackgroundImageField";
import { EmailBuilderSettingsImageOverlayFields } from "./controls/EmailBuilderSettingsImageOverlayFields";
import { EmailBuilderSettingsBorderFields } from "./controls/EmailBuilderSettingsBorderFields";
import { EmailBuilderSettingsCornerRoundingSection } from "./controls/EmailBuilderSettingsCornerRoundingSection";
import { EmailBuilderSettingsAccordion } from "./accordion/EmailBuilderSettingsAccordion";
import { APPEARANCE_NESTED_DEFAULT_EXPANDED } from "./settingsPanelDefaults";
import {
  isSurfaceBackgroundInactive,
  isSurfaceBorderInactive,
  surfaceBorderAddDefault,
} from "./settingsSectionActivation";

type SurfaceAppearance = {
  backgroundColor: string;
  backgroundImage?: string;
  backgroundImageOverlay?: ImageOverlay;
  border: BorderStyle;
  borderRadius: CornerRadius;
};

type SurfaceAppearanceBaselines = {
  backgroundColor: string;
  border: BorderStyle;
  borderRadius: CornerRadius;
};

type SurfaceAppearanceSectionProps = {
  value: SurfaceAppearance;
  baselines: SurfaceAppearanceBaselines;
  onChange: (patch: Partial<SurfaceAppearance>) => void;
  allowTransparentBackground?: boolean;
  showBackgroundImage?: boolean;
};

export function SurfaceBackgroundFields({
  value,
  baselines,
  onChange,
  allowTransparentBackground = false,
  showBackgroundImage = false,
}: SurfaceAppearanceSectionProps) {
  const { t } = useTranslation();
  const hasBackgroundOverride = value.backgroundColor !== baselines.backgroundColor;

  return (
    <>
      <EmailBuilderColorOverrideField
        label={t("Color")}
        color={value.backgroundColor === "transparent" ? "#FFFFFF" : value.backgroundColor}
        hasOverride={hasBackgroundOverride}
        emptyAsNone={allowTransparentBackground}
        showFieldLabel={false}
        onChange={backgroundColor => onChange({ backgroundColor })}
        onReset={() => onChange({ backgroundColor: baselines.backgroundColor })}
      />
      {showBackgroundImage && value.backgroundImage !== undefined ? (
        <BackgroundImageField
          value={value.backgroundImage}
          onChange={backgroundImage => onChange({ backgroundImage })}
        />
      ) : null}
      {showBackgroundImage && value.backgroundImage?.trim().length ? (
        <EmailBuilderSettingsImageOverlayFields
          value={value.backgroundImageOverlay ?? DEFAULT_IMAGE_OVERLAY}
          onChange={backgroundImageOverlay => onChange({ backgroundImageOverlay })}
        />
      ) : null}
    </>
  );
}

export function SurfaceCornerRoundingFields({
  value,
  baselines,
  onChange,
}: Pick<SurfaceAppearanceSectionProps, "value" | "baselines" | "onChange">) {
  const defaultRadius = baselines.borderRadius;
  const resolvedRadius = value.borderRadius ?? defaultRadius;

  return (
    <EmailBuilderSettingsCornerRoundingSection
      value={resolvedRadius}
      onChange={borderRadius => {
        onChange({ borderRadius: { ...borderRadius } });
      }}
    />
  );
}

export function SurfaceBorderFields({
  value,
  onChange,
}: Pick<SurfaceAppearanceSectionProps, "value" | "onChange">) {
  return <EmailBuilderSettingsBorderFields border={value.border} onChange={border => onChange({ border })} />;
}

export function SurfaceAppearanceSection({
  value,
  baselines,
  onChange,
  allowTransparentBackground = false,
  showBackgroundImage = false,
}: SurfaceAppearanceSectionProps) {
  const { t } = useTranslation();
  const backgroundActive = !isSurfaceBackgroundInactive(
    value.backgroundColor,
    value.backgroundImage,
  );
  const borderActive = !isSurfaceBorderInactive(value.border);

  return (
    <EmailBuilderSettingsAccordion defaultExpandedSectionIds={APPEARANCE_NESTED_DEFAULT_EXPANDED} nested>
      <EmailBuilderSettingsAccordion.Section
        sectionId="appearance-background"
        title={t("Background")}
        variant="nested"
        activation={{
          mode: "optional",
          isActive: backgroundActive,
          onAdd: () => {},
        }}
      >
        <SurfaceBackgroundFields
          value={value}
          baselines={baselines}
          onChange={onChange}
          allowTransparentBackground={allowTransparentBackground}
          showBackgroundImage={showBackgroundImage}
        />
      </EmailBuilderSettingsAccordion.Section>
      <EmailBuilderSettingsAccordion.Section
        sectionId="appearance-border"
        title={t("Border")}
        variant="nested"
        activation={{
          mode: "optional",
          isActive: borderActive,
          onAdd: () => onChange({ border: surfaceBorderAddDefault() }),
        }}
      >
        <SurfaceBorderFields value={value} onChange={onChange} />
      </EmailBuilderSettingsAccordion.Section>
    </EmailBuilderSettingsAccordion>
  );
}

export function SurfaceAppearanceFields(props: SurfaceAppearanceSectionProps) {
  return (
    <>
      <SurfaceBackgroundFields {...props} />
      <SurfaceBorderFields value={props.value} onChange={props.onChange} />
      <SurfaceCornerRoundingFields value={props.value} baselines={props.baselines} onChange={props.onChange} />
    </>
  );
}
